// HTML <-> Slate value helpers for the Plate editor on ArticleDetailPage.
//
// - htmlToValue: parse stored article HTML into a Plate value (load path).
// - valueToHtml: serialize the editor value back to clean HTML for the CMS
//   (save path). Uses the static kits (*-base-kit + *-node-static components),
//   strips transient AI/comment/suggestion marks so they never reach the CMS.
// - countWords / valueToPlainText: shared tree walks used for the live word
//   count and the Copy All button.

import { createStaticEditor, serializeHtml } from 'platejs/static';

import { BaseEditorKit } from '@/components/editor/editor-base-kit';
import { EditorStatic } from '@/components/ui/editor-static';

let _htmlEditor = null;

function getDeserializeEditor() {
  if (!_htmlEditor) {
    _htmlEditor = createStaticEditor({ plugins: BaseEditorKit });
  }
  return _htmlEditor;
}

export function htmlToValue(html) {
  if (!html) return undefined; // let the editor use its default empty value

  const editor = getDeserializeEditor();
  return editor.api.html.deserialize({ element: html });
}

// Text-node keys produced by AI insertions, comments/discussions and
// suggestions — plus element-level suggestion meta. They must not leak
// into saved HTML.
const TRANSIENT_KEYS = new Set(['ai', 'comment', 'suggestion']);
const TRANSIENT_PREFIXES = ['comment_', 'suggestion_', 'discussion_'];

function stripTransient(node) {
  const out = Array.isArray(node) ? node.map(stripTransient) : { ...node };

  for (const key of Object.keys(out)) {
    if (
      TRANSIENT_KEYS.has(key) ||
      TRANSIENT_PREFIXES.some((p) => key.startsWith(p))
    ) {
      delete out[key];
    }
  }

  if (Array.isArray(out.children)) {
    out.children = stripTransient(out.children);
  }

  // Deep-clean caption-ish nested values (img.caption etc.)
  for (const [key, value] of Object.entries(out)) {
    if (Array.isArray(value) && key !== 'children') {
      out[key] = value.map((v) =>
        v && typeof v === 'object' ? stripTransient([v])[0] : v
      );
    }
  }

  return out;
}

export async function valueToHtml(value) {
  const editor = createStaticEditor({
    plugins: BaseEditorKit,
    value: () => stripTransient(value ?? []),
  });

  return serializeHtml(editor, {
    editorComponent: EditorStatic,
    stripClassNames: true,
    stripDataAttributes: true,
  });
}

function* textNodes(node) {
  if (!node) return;
  if (typeof node.text === 'string') {
    yield node.text;
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      yield* textNodes(child);
    }
  }
}

// One paragraph per top-level block — matches the old BlockNote Copy All
// behaviour (blocks joined with a blank line).
export function valueToPlainText(value) {
  return (value ?? [])
    .map((block) => [...textNodes(block)].join(''))
    .filter(Boolean)
    .join('\n\n');
}

export function countWords(value) {
  const text = valueToPlainText(value).trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}
